import React from 'react';
import { Modal, Box, Typography, Button } from '@mui/material';
import { CommandIndexProvidersQuery, GenerateVisitsWarningsMutation, ScheduleOverrideVisitMutationVariables, useScheduleOverrideVisitMutation } from '~/graphql/__generated__/graphql';
import { format } from 'date-fns';
import VisitWarningList from '../common/VisitWarningList';
import { gql } from '@apollo/client';
import { useSnackbar } from '~/hooks/useSnackbar';
import { useOrgConfig, OrgConfig, ServiceLine, VisitType } from "~/providers/OrgConfigProvider";
import VisitResourceWarning from '../common/VisitResourceWarning';
import _ from 'lodash';
import { hasStateLicense, validateProviderServiceLine } from '~/utils/visitValidationUtils';
import { useZonedDateFormat } from '~/providers/ZonedDateFormatProvider';

interface VisitWarningsProps {
  visitWarningLog: GenerateVisitsWarningsMutation["generateVisitWarnings"];
  open: boolean;
  duration: number;
  handleClose: () => void;
  onSuccess: (visitId: number) => void;
  providersList: CommandIndexProvidersQuery["commandIndexProviders"]["providers"];
  startTime: Date;
  selectedPatient: { id: number, title: string, addressTimezone: string, addressState?: string | null };
  selectedProviders: CommandIndexProvidersQuery["commandIndexProviders"]["providers"];
  currentServiceLine: ServiceLine;
  selectedVisitType: VisitType;
  organizationalUnitId?: number;
  serviceIds: number[];
  resources: {
    startTime: string | null,
    duration: number,
    providerId: number,
    offset: number,
    visitResourceRequirementId: number
  }[];
  serviceInstructions: string;
  visitNote: string;
  reschedulingVisitId?: number;
}

const getUnlicensedProviderIds = (
  state: string | null | undefined,
  selectedProviders: VisitWarningsProps["selectedProviders"],
  orgConfig: OrgConfig,
) => {
  if (!state) return new Set<number>();

  const ids = new Set<number>();
  selectedProviders.forEach((provider: CommandIndexProvidersQuery["commandIndexProviders"]["providers"][number]) => {
    const providerType = provider.providerTypeId ? orgConfig.providerTypes[provider.providerTypeId] : null;
    const requiresLicense = (providerType as { requireStateLicense?: boolean })?.requireStateLicense;

    if (!requiresLicense) return;

    const providerTraits = (provider as { traits?: { trait: string; value: string }[] }).traits;
    if (!hasStateLicense(providerTraits, state)) {
      ids.add(provider.id);
    }
  });
  return ids;
}

const getServiceLineMismatchProviderIds = (
  selectedProviders: VisitWarningsProps["selectedProviders"],
  currentServiceLine: ServiceLine,
) => {
  const currentVisitTypeIds = new Set(
    currentServiceLine.visitTypeServiceLines.map((vtsl) => vtsl.visitTypeId)
  );

  const ids = new Set<number>();
  selectedProviders.forEach((provider: CommandIndexProvidersQuery["commandIndexProviders"]["providers"][number]) => {
    const providerSupportsServiceLine = provider.serviceLineProviders.some((slp) =>
      slp.serviceLine?.visitTypeServiceLines.some((vtsl) => currentVisitTypeIds.has(vtsl.visitTypeId))
    );

    const hasServiceLineMismatch = validateProviderServiceLine(
      {
        firstName: provider.firstName,
        lastName: provider.lastName,
        allowedServiceLineIds: providerSupportsServiceLine ? [currentServiceLine.id] : [],
      },
      [{ id: currentServiceLine.id, name: currentServiceLine.name }]
    ).length > 0;

    if (hasServiceLineMismatch) {
      ids.add(provider.id);
    }
  });
  return ids;
}

const VisitWarningsModal: React.FC<VisitWarningsProps> = ({
  open, handleClose, startTime, visitWarningLog, selectedPatient, selectedProviders, currentServiceLine, selectedVisitType, organizationalUnitId, duration, serviceIds, resources, onSuccess, reschedulingVisitId, serviceInstructions, visitNote,
}) => {
  const isEditMode = !!reschedulingVisitId;
  const orgConfig = useOrgConfig();
  const formattedDate = (startTime ? format(startTime, 'PP') : '');
  const patientTimezone = selectedPatient.addressTimezone;

  const { timeLabelWithZone } = useZonedDateFormat();

  const formattedTime = !startTime ? '' : timeLabelWithZone(startTime, true, patientTimezone);

  const { setErrorMessage: displayError } = useSnackbar();

  const [scheduleOverrideVisit, { loading: schedulingVisit }] = useScheduleOverrideVisitMutation({
    refetchQueries: ["CommandIndexCombinedData", "VisitLocations"],
    awaitRefetchQueries: true,
  });

  const visitWarnings = visitWarningLog?.visitWarnings;
  const visitWarningLogId = visitWarningLog?.visitWarningLogId;
  const baseVisitResourceWarnings = visitWarningLog?.visitResourceWarnings;

  const unlicensedProviderIds = getUnlicensedProviderIds(selectedPatient.addressState, selectedProviders, orgConfig);
  const serviceLineMismatchProviderIds = getServiceLineMismatchProviderIds(selectedProviders, currentServiceLine);

  // Merge license and service line warnings into visitResourceWarnings for each provider
  const visitResourceWarnings = baseVisitResourceWarnings?.map(resourceWarning => {
    const hasLicenseIssue = unlicensedProviderIds.has(resourceWarning.providerId);
    const hasServiceLineIssue = serviceLineMismatchProviderIds.has(resourceWarning.providerId);
    
    if (hasLicenseIssue || hasServiceLineIssue) {
      return {
        ...resourceWarning,
        providerNotLicensed: hasLicenseIssue ? selectedPatient.addressState : undefined,
        providerServiceLineMismatch: hasServiceLineIssue ? currentServiceLine.name : undefined,
        severity: Math.max(resourceWarning.severity, 2),
      };
    }
    return resourceWarning;
  }) ?? [];

  const hasWarnings = [...[visitWarnings], ...visitResourceWarnings].filter(warning => warning && warning.severity > 0).length > 0;

  const handleSubmit = async () => {
    try {
      if (!visitWarningLogId) { return }

      const variables: ScheduleOverrideVisitMutationVariables = {
        patientId: selectedPatient.id,
        serviceLineId: currentServiceLine.id,
        organizationalUnitId: organizationalUnitId ?? null,
        visitTypeId: selectedVisitType.id,
        startTime: startTime.toISOString(),
        resources: resources.map((resource) => ({
          providerId: resource.providerId,
          resourceRequirementId: resource.visitResourceRequirementId,
          duration: resource.duration,
          offset: resource.offset,
          shiftId: null,
        })),
        serviceIds,
        duration,
        visitWarningLogId,
        reschedulingVisitId: reschedulingVisitId || null,
        serviceInstructions,
        note: visitNote,
      };
      const result = await scheduleOverrideVisit({ variables });
      const visitId = result.data?.scheduleOverrideVisit?.id;
      onSuccess(visitId || 0);
    } catch (error) {
      const errorMessage = 'An unexpected error occurred';
      displayError(errorMessage);
    }
  };

  return (
    <Modal
      open={open}
      onClose={handleClose}
      aria-labelledby="modal-title"
      aria-describedby="modal-description"
    >
      <Box sx={{
        position: 'absolute',
        top: '50%',
        left: '50%',
        transform: 'translate(-50%, -50%)',
        minWidth: 400,
        bgcolor: 'background.paper',
        p: 4
      }}>
        <Typography id="modal-title" variant="h6" component="h2">
          {isEditMode ? 'Edit' : 'Create'} Visit for {formattedDate} at {formattedTime}
        </Typography>
        <Box id="modal-description" sx={{ mt: 2 }}>
          <Typography variant='subtitle2'>Patient: {selectedPatient.title}</Typography>
          <Typography variant='subtitle2'>Service Line: {currentServiceLine?.name}</Typography>
          <Typography variant='subtitle2'>Visit Type: {selectedVisitType?.name}</Typography>

          <Box sx={{ my: 3 }}>
            <Box sx={{ my: 2 }}>
              <Typography variant='subtitle2'>Resources</Typography>
              {selectedProviders?.map(provider => (
                <Typography key={provider.id} variant='body2'>{provider.firstName} {provider.lastName} ({_.startCase(provider?.providerType?.name)})</Typography>
              ))}
            </Box>
          </Box>

          {visitWarnings && <VisitWarningList visitWarnings={visitWarnings} resources={selectedProviders.map(p => {return {provider: p}})} />}
          {visitResourceWarnings?.map((resourceWarning) => {
            const provider = selectedProviders.find(p => p.id === resourceWarning.providerId)
            if (!provider) return null;
            return (
              <VisitResourceWarning key={provider.id} visitResourceWarning={resourceWarning} provider={provider} />
            )
          })}
        </Box>

        <Box justifySelf='flex-end'>
          <Button
            onClick={() => {
              handleClose();
            }}
            sx={{ mr: 4 }}
          >
            Back
          </Button>
          <Button
            variant='contained'
            size='large'
            // loading={schedulingVisit || updatingVisit}
            loading={schedulingVisit}
            onClick={handleSubmit}
          >
            {isEditMode ? 'Save Changes' : 'Create Visit'}{hasWarnings && ` Anyway`}
          </Button>
        </Box>
      </Box>
    </Modal>
  );
};

export default VisitWarningsModal;

gql`
  mutation ScheduleOverrideVisit(
    $patientId: Int!,
    $serviceLineId: Int!,
    $organizationalUnitId: Int,
    $visitTypeId: Int!,
    $startTime: String!,
    $resources: [NewOverrideVisitResourceInput!]!,
    $serviceIds: [Int!]!,
    $duration: Int!,
    $visitWarningLogId: Int!,
    $reschedulingVisitId: Int,
    $serviceInstructions: String,
    $note: String
  )
  @permission(requires: createOverrideVisits, check: [userInServiceLine])
  {
    scheduleOverrideVisit(
      patientId: $patientId,
      serviceLineId: $serviceLineId,
      organizationalUnitId: $organizationalUnitId,
      visitTypeId: $visitTypeId,
      startTime: $startTime,
      resources: $resources,
      serviceIds: $serviceIds,
      duration: $duration,
      visitWarningLogId: $visitWarningLogId,
      reschedulingVisitId: $reschedulingVisitId,
      serviceInstructions: $serviceInstructions,
      note: $note
    ) {
      id
    }
  }
`;
